import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { API_BASE } from '../config';
import { AuthCard, AuthCardTitle, AuthCardDescription, AuthFormError, AuthFormSuccess } from './AuthCard';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

export const ResetPassword: React.FC = () => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError(t('invalid_reset_token'));
    }
  }, [token, t]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError(t('passwords_not_match'));
      return;
    }

    if (password.length < 8) {
      setError(t('password_too_short'));
      return;
    }

    if (!token) {
      setError(t('invalid_reset_token'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reset password');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-background pt-8">
        <AuthCard>
          <AuthCardTitle>{t('reset_password_title')}</AuthCardTitle>
          <AuthFormError>{t('invalid_reset_token')}</AuthFormError>
          <Button type="button" variant="inverse" className="w-full" onClick={() => navigate('/')}>
            {t('back')}
          </Button>
        </AuthCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-8">
      <AuthCard>
        <AuthCardTitle>{t('reset_password_title')}</AuthCardTitle>
        <AuthCardDescription>{t('reset_password_description')}</AuthCardDescription>
        {success ? (
          <div>
            <AuthFormSuccess>{t('password_reset_success')}</AuthFormSuccess>
            <Button type="button" variant="inverse" className="w-full" onClick={() => navigate('/')}>
              {t('login')}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <Label htmlFor="password" className="mb-2 block">
                {t('password')}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div className="mb-6">
              <Label htmlFor="confirmPassword" className="mb-2 block">
                {t('confirm_password')}
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            {error && <AuthFormError>{error}</AuthFormError>}
            <Button type="submit" variant="inverse" className="mb-4 w-full" disabled={isLoading}>
              {isLoading ? t('resetting_password') : t('reset_password')}
            </Button>
          </form>
        )}
      </AuthCard>
    </div>
  );
};
